import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import { ProfinderLog } from '@engine/models/admin'

import { Table } from '#components/Table'
import { formatDateTime, formatRelativeTime } from '#utils/format'

import { ProfinderStatusBadge } from './ProfinderStatusBadge'

interface ProfinderLogsTableProps {
  logs: ProfinderLog[]
  onSelect: (log: ProfinderLog) => void
  selectedLogId?: string
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
}

const columnHelper = createColumnHelper<ProfinderLog>()

const ProfinderLogsTable = ({
  logs,
  onSelect,
  selectedLogId,
  infiniteScrollRef,
  isFetchingMore,
}: ProfinderLogsTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'externalId',
        header: 'Load ID',
        cell: info => {
          const log = info.row.original
          const loadNum = log.data?.refNums.find(
            r => r.type === 'loadNum',
          )?.value
          return (
            <span className="align-top font-mono text-sm">
              {loadNum || log.data?.externalId || '-'}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: 'result',
        header: 'Result',
        cell: info => {
          const status = info.row.original.data?.status
          return <ProfinderStatusBadge status={status} small />
        },
      }),
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: info => (
          <div className="align-top">
            <div>{formatDateTime(info.getValue())}</div>
            <div className="text-xs opacity-70">
              {formatRelativeTime(info.getValue())}
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'carrier',
        header: 'Carrier',
        cell: info => {
          const log = info.row.original
          return (
            <span className="badge badge-sm badge-info uppercase align-top">
              {log.data?.scac ?? '-'}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: 'proNum',
        header: 'Pro#',
        cell: info => {
          const log = info.row.original
          return (
            <span className="align-top font-mono text-sm">
              {log.data?.proNum || '-'}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: 'reason',
        header: 'Reason',
        cell: info => {
          const log = info.row.original
          return (
            <span className="align-top font-mono text-sm">
              {log.data?.reason || '-'}
            </span>
          )
        },
      }),
    ],
    [],
  )

  if (logs.length === 0) {
    return null
  }

  return (
    <Table
      data={logs}
      columns={columns}
      getRowId={log => log.id}
      onRowClick={onSelect}
      selectedId={selectedLogId}
      infiniteScrollRef={infiniteScrollRef}
      isFetchingMore={isFetchingMore}
    />
  )
}

export { ProfinderLogsTable }
